import { useHistory, useRouteMatch } from 'react-router-dom';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { isDate, isNil } from 'lodash/fp';
import { getUrlParams, setUrlParams } from '../../../../utils/helpers/urlParams';
import { DELIVERY_LISTS_ROWS_PER_PAGE } from './constants';

type ParamType = false | string | null | number | Date | string | string[];

const useSearchParams = () => {
    const history = useHistory();
    const { isExact } = useRouteMatch();

    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [supplierId, setSupplierId] = useState<string | null>(null);
    const [deliveryDateUtc, setDeliveryDateUtc] = useState<Date | null>(null);
    const [rowsPerPage, setRowsPerPage] = useState(DELIVERY_LISTS_ROWS_PER_PAGE);
    const [page, setPage] = useState(0);

    const handleUrlParamSet = <T extends ParamType>(setState: Dispatch<SetStateAction<T>>) =>
        (paramObj: Record<string, ParamType>) => {
            if (isExact) {
                const paramName = Object.keys(paramObj)[0];
                const param = paramObj[paramName];
                setState(param as T);
                const finalParam = isDate(param) ? param.toISOString() : param;
                const newUrlParams = setUrlParams(history.location.search, {
                    [paramName]: isNil(finalParam) ? '' : finalParam || '',
                });

                history.push({ search: newUrlParams.toString() });
            }
        };

    useEffect(() => {
        if (isExact) {
            const [
                invoiceNumberParam,
                supplierIdParam,
                deliveryDateUtcParam,
                rowsPerPageParam,
                pageParam,
            ] = getUrlParams({
                searchString: history.location.search,
                params: ['invoiceNumber', 'supplierId', 'deliveryDateUtc', 'rowsPerPage', 'page']
            });

            setInvoiceNumber(invoiceNumberParam || '');
            setSupplierId(supplierIdParam || null);
            setDeliveryDateUtc(deliveryDateUtcParam ? new Date(deliveryDateUtcParam) : null);
            setRowsPerPage(rowsPerPageParam ? parseInt(rowsPerPageParam, 10) : DELIVERY_LISTS_ROWS_PER_PAGE);
            setPage(pageParam ? parseInt(pageParam, 10) : 0);

            const newUrlParams = setUrlParams(history.location.search, {
                invoiceNumber: invoiceNumberParam || '',
                supplierId: supplierIdParam || '',
                deliveryDateUtc: deliveryDateUtc ? deliveryDateUtc.toUTCString() : '',
                rowsPerPage: rowsPerPageParam || DELIVERY_LISTS_ROWS_PER_PAGE,
                page: pageParam || 0,
            });

            history.push({ search: newUrlParams.toString() });
        }
    // eslint-disable-next-line
    }, [isExact]);

    const handleSetInvoiceNumber = (p: ParamType) => handleUrlParamSet(setInvoiceNumber)({ invoiceNumber: p });
    const handleSetSupplierId = (p: ParamType) => handleUrlParamSet(setSupplierId)({ supplierId: p });
    const handleSetDeliveryDateUtc = (p: ParamType) => handleUrlParamSet(setDeliveryDateUtc)({ deliveryDateUtc: p });
    const handleSetRowsPerPage = (p: ParamType) => handleUrlParamSet(setRowsPerPage)({ rowsPerPage: p });
    const handleSetPage = (r: ParamType) => handleUrlParamSet(setPage)({ page: r });

    return {
        invoiceNumber, setInvoiceNumber: handleSetInvoiceNumber,
        supplierId, setSupplierId: handleSetSupplierId,
        deliveryDateUtc, setDeliveryDateUtc: handleSetDeliveryDateUtc,
        rowsPerPage, setRowsPerPage: handleSetRowsPerPage,
        page, setPage: handleSetPage,
    };
};

export default useSearchParams;
